<template>
  <transition name="filter-employees-readiness-modal" appear>
    <div
      v-if="value"
      class="filter-employees-readiness-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="filter-employees-readiness-modal-title"
      @mousedown.self="handleClose"
    >
      <div class="filter-employees-readiness-modal__card">
        <div class="filter-employees-readiness-modal__header">
          <span
            id="filter-employees-readiness-modal-title"
            class="filter-employees-readiness-modal__title"
          >
            Filter employees
          </span>
          <button
            type="button"
            class="filter-employees-readiness-modal__close-btn"
            aria-label="Close"
            @click="handleClose"
          >
            <Icon name="popup_close" />
          </button>
        </div>

        <!-- ER-10506 / MDP-552 (Rule 5): Excluded radio + Department picker replace the
             legacy Recalculation radio group. Recalculation is removed entirely. -->
        <div class="filter-employees-readiness-modal__filters-row">
          <div
            class="filter-employees-readiness-modal__column filter-employees-readiness-modal__column--radio"
          >
            <RadioInput
              label="Excluded"
              :availableItems="excludedOptions"
              :checkedItem="excludedCheckedItem"
              @changeradio="onExcludedChange"
            />
          </div>
        </div>

        <div class="filter-employees-readiness-modal__divider" />

        <div class="filter-employees-readiness-modal__department">
          <span class="filter-employees-readiness-modal__column-title">Department</span>

          <DepartmentPickerField
            browse-list
            browse-list-requires-query
            :value="department"
            :departments="departments"
            :loading="loading"
            @input="setDepartment"
            @search="onSearch"
            @clear="onClear"
          />

          <p v-if="!department" class="filter-employees-readiness-modal__hint">
            Leave empty to show employees across the whole organization.
          </p>

          <template v-else>
            <div
              class="filter-employees-readiness-modal__scope-toggle"
              role="radiogroup"
              aria-label="Department scope"
            >
              <button
                type="button"
                class="filter-employees-readiness-modal__scope-option"
                :class="{
                  'filter-employees-readiness-modal__scope-option--active': scope === 'direct',
                }"
                role="radio"
                :aria-checked="scope === 'direct'"
                @click="setScope('direct')"
              >
                This department only
              </button>
              <span class="filter-employees-readiness-modal__scope-divider" aria-hidden="true" />
              <button
                type="button"
                class="filter-employees-readiness-modal__scope-option"
                :class="{
                  'filter-employees-readiness-modal__scope-option--active': scope === 'subtree',
                }"
                role="radio"
                :aria-checked="scope === 'subtree'"
                @click="setScope('subtree')"
              >
                Include all sub-departments
              </button>
            </div>
            <p class="filter-employees-readiness-modal__hint">
              {{ scopeHint }}
            </p>
          </template>
        </div>

        <div class="filter-employees-readiness-modal__divider" />

        <div class="filter-employees-readiness-modal__footer">
          <button
            type="button"
            class="filter-employees-readiness-modal__reset"
            @click="handleReset"
          >
            Reset to defaults
          </button>
          <StandardButton
            type="magenta"
            variant="primary"
            size="small"
            label="Apply"
            @click="handleApply"
          />
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from "vue";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import DepartmentPickerField from "@components/DepartmentPickerField/DepartmentPickerField.vue";
import RadioInput from "@components/Radio/RadioInput.vue";
import {
  EXCLUDE_FROM_AVG_LABEL_BY_VALUE,
  getExcludeFromAvgValueFromLabel,
} from "./employeeReadinessFilterPayload";

const props = defineProps({
  value: { type: Boolean, default: false },
  /** "" | "excluded" | "not_excluded" — the persisted Excluded radio selection. */
  initialExcluded: { type: String, default: "" },
  initialDepartment: { type: Object, default: null },
  initialScope: {
    type: String,
    default: "subtree",
    validator: (value) => ["direct", "subtree"].includes(value),
  },
  departments: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
});

const DEFAULT_EXCLUDED = "";
const DEFAULT_SCOPE = "subtree";

const emit = defineEmits(["input", "close", "apply", "reset", "search", "clear"]);

const excludedOptions = [{ label: "No Filter" }, { label: "Excluded" }, { label: "Not Excluded" }];

const excluded = ref(DEFAULT_EXCLUDED);
const department = ref(props.initialDepartment);
const scope = ref(props.initialScope);

const excludedCheckedItem = computed(
  () => EXCLUDE_FROM_AVG_LABEL_BY_VALUE[excluded.value] || "No Filter"
);

const scopeHint = computed(() =>
  scope.value === "subtree"
    ? "Showing this department plus every department nested beneath it, at any depth."
    : "Showing employees belonging directly to this department only."
);

watch(
  () => props.value,
  (open) => {
    if (open) {
      syncFromProps();
      emit("search", "");
    }
  },
  { immediate: true }
);

function syncFromProps() {
  excluded.value = props.initialExcluded || DEFAULT_EXCLUDED;
  department.value = props.initialDepartment;
  scope.value = props.initialScope;
}

function onExcludedChange(payload) {
  excluded.value = getExcludeFromAvgValueFromLabel(payload?.value);
}

function setDepartment(dept) {
  department.value = dept;
  if (!dept) scope.value = DEFAULT_SCOPE;
}

function setScope(val) {
  scope.value = val;
}

function onSearch(query) {
  emit("search", query);
}

function onClear() {
  emit("clear");
  emit("search", "");
}

function handleReset() {
  excluded.value = DEFAULT_EXCLUDED;
  department.value = null;
  scope.value = DEFAULT_SCOPE;
  emit("reset");
  emit("search", "");
}

function handleApply() {
  emit("apply", {
    excluded: excluded.value,
    department: department.value,
    scope: department.value ? scope.value : null,
  });
}

function handleClose() {
  emit("input", false);
  emit("close");
}

function handleKeydown(event) {
  if (event.key === "Escape" && props.value) handleClose();
}

onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
});
</script>

<style scoped>
.filter-employees-readiness-modal-enter-active,
.filter-employees-readiness-modal-leave-active {
  transition: opacity 180ms ease;
}

.filter-employees-readiness-modal-enter-from,
.filter-employees-readiness-modal-leave-to {
  opacity: 0;
}

.filter-employees-readiness-modal-enter-active .filter-employees-readiness-modal__card,
.filter-employees-readiness-modal-leave-active .filter-employees-readiness-modal__card {
  transition: transform 200ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease;
}

.filter-employees-readiness-modal-enter-from .filter-employees-readiness-modal__card,
.filter-employees-readiness-modal-leave-to .filter-employees-readiness-modal__card {
  transform: translateY(8px) scale(0.98);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .filter-employees-readiness-modal-enter-active,
  .filter-employees-readiness-modal-leave-active,
  .filter-employees-readiness-modal-enter-active .filter-employees-readiness-modal__card,
  .filter-employees-readiness-modal-leave-active .filter-employees-readiness-modal__card {
    transition: none !important;
  }
}

.filter-employees-readiness-modal__overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(16, 24, 40, 0.5);
  font-family: Inter, sans-serif;
}

.filter-employees-readiness-modal__card {
  width: 100%;
  max-width: 640px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 24px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 8px 8px -4px rgba(16, 24, 40, 0.03), 0 20px 24px -4px rgba(16, 24, 40, 0.08);
  box-sizing: border-box;
}

.filter-employees-readiness-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.filter-employees-readiness-modal__title {
  color: #101828;
  font-size: 18px;
  font-weight: 600;
  line-height: normal;
}

.filter-employees-readiness-modal__close-btn {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px;
  margin: -4px;
  cursor: pointer;
  color: #98a2b3;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.filter-employees-readiness-modal__close-btn:hover {
  background: rgba(152, 162, 179, 0.12);
}

.filter-employees-readiness-modal__close-btn:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}

.filter-employees-readiness-modal__filters-row {
  display: flex;
  gap: 32px;
  align-items: flex-start;
  width: 100%;
}

.filter-employees-readiness-modal__column {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-employees-readiness-modal__column-title {
  padding-left: 8px;
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.filter-employees-readiness-modal__divider {
  height: 1px;
  width: 100%;
  background: #e5e7eb;
}

.filter-employees-readiness-modal__department {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.filter-employees-readiness-modal__hint {
  margin: 0;
  color: #98a2b3;
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
}

.filter-employees-readiness-modal__scope-toggle {
  display: flex;
  width: 100%;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  overflow: hidden;
}

.filter-employees-readiness-modal__scope-option {
  flex: 1 1 0;
  min-width: 0;
  appearance: none;
  border: 0;
  background: #fff;
  padding: 10px 16px;
  color: #344054;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  cursor: pointer;
  text-align: center;
}

.filter-employees-readiness-modal__scope-option--active {
  background: #f9f5ff;
  color: #6941c6;
}

.filter-employees-readiness-modal__scope-option:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: -2px;
}

.filter-employees-readiness-modal__scope-divider {
  width: 1px;
  align-self: stretch;
  background: #d0d5dd;
}

.filter-employees-readiness-modal__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.filter-employees-readiness-modal__reset {
  appearance: none;
  border: 0;
  background: transparent;
  padding: 4px;
  margin: -4px;
  color: #475467;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: normal;
  cursor: pointer;
  border-radius: 6px;
}

.filter-employees-readiness-modal__reset:hover {
  color: #344054;
  background: rgba(152, 162, 179, 0.08);
}

.filter-employees-readiness-modal__reset:focus-visible {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}
</style>
